/** @jsxImportSource @emotion/react */
import {
  Box,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  Button,
  Alert,
  Tooltip,
} from '@mui/material'
import { Delete, ExpandMore, Add, Link as LinkIcon, Code, Info } from '@mui/icons-material'
import { StepSection } from '@app/components/Risk/types'
import { accordionStyles, formLabelStyles } from './StoryBuilder.styles'

interface SectionEditorProps {
  section: StepSection
  index: number
  onUpdate: (updates: Partial<StepSection>) => void
  onDelete: () => void
  canDelete: boolean
}

export const SectionEditor = ({
  section,
  index,
  onUpdate,
  onDelete,
  canDelete,
}: SectionEditorProps) => {
  const theme = useTheme()

  const handleAddButton = () => {
    const currentButtons = section.buttons || []
    onUpdate({
      buttons: [...currentButtons, { url: '', title: '' }],
    })
  }

  const handleUpdateButton = (buttonIndex: number, field: 'url' | 'title', value: string) => {
    const currentButtons = section.buttons || []
    const updatedButtons = [...currentButtons]
    updatedButtons[buttonIndex] = {
      ...updatedButtons[buttonIndex],
      [field]: value,
    }
    onUpdate({ buttons: updatedButtons })
  }

  const handleDeleteButton = (buttonIndex: number) => {
    const currentButtons = section.buttons || []
    onUpdate({
      buttons: currentButtons.filter((_, i) => i !== buttonIndex),
    })
  }

  return (
    <Accordion css={accordionStyles(theme)} defaultExpanded={index === 0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ fontWeight: 500 }}>
            Section {index + 1}
            {section.header &&
              `: ${section.header.substring(0, 30)}${section.header.length > 30 ? '...' : ''}`}
          </Box>
          {canDelete && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              sx={{ mr: 1 }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Box css={formLabelStyles(theme)}>Header</Box>
            <TextField
              fullWidth
              size="small"
              placeholder="Section header (optional)"
              value={section.header || ''}
              onChange={(e) => onUpdate({ header: e.target.value })}
            />
          </Box>

          <Box>
            <Box css={formLabelStyles(theme)}>Content</Box>
            <TextField
              fullWidth
              multiline
              rows={4}
              size="small"
              placeholder="Section content text..."
              value={section.content || ''}
              onChange={(e) => onUpdate({ content: e.target.value })}
            />
          </Box>

          <Box>
            <Box css={formLabelStyles(theme)}>Wide Image URL</Box>
            <TextField
              fullWidth
              size="small"
              placeholder="https://example.com/image.jpg"
              value={section.wideImage || ''}
              onChange={(e) => onUpdate({ wideImage: e.target.value || undefined })}
            />
          </Box>

          <Box>
            <Box css={formLabelStyles(theme)}>Side Image URL</Box>
            <TextField
              fullWidth
              size="small"
              placeholder="https://example.com/image.jpg"
              value={section.sideImage || ''}
              onChange={(e) => onUpdate({ sideImage: e.target.value || undefined })}
            />
          </Box>

          <Box>
            <Box css={formLabelStyles(theme)}>Video URL</Box>
            <TextField
              fullWidth
              size="small"
              placeholder="https://example.com/video.mp4"
              value={section.videoUrl || ''}
              onChange={(e) => onUpdate({ videoUrl: e.target.value || undefined })}
            />
          </Box>

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Code fontSize="small" color="primary" />
              <Box css={formLabelStyles(theme)} sx={{ mb: 0 }}>
                Embed URL
              </Box>
              <Tooltip title="Embed Google Forms, YouTube videos, or other iframe content. Paste the src URL from the iframe embed code.">
                <Info fontSize="small" sx={{ color: theme.palette.grey[400], cursor: 'help' }} />
              </Tooltip>
            </Box>
            <Box
              sx={{
                color: '#f00',
                fontSize: '0.875rem',
                fontStyle: 'italic',
              }}
            >
              NOT YET SUPPORTED
            </Box>
            <TextField
              disabled={true}
              fullWidth
              size="small"
              placeholder="https://docs.google.com/forms/d/e/.../viewform?embedded=true"
              value={section.embedUrl || ''}
              onChange={(e) => onUpdate({ embedUrl: e.target.value || undefined })}
              sx={{ mt: 0.5 }}
            />
            {section.embedUrl && (
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Embed Height (px)"
                  placeholder="600"
                  value={section.embedHeight || ''}
                  onChange={(e) =>
                    onUpdate({ embedHeight: e.target.value ? parseInt(e.target.value) : undefined })
                  }
                  InputProps={{ inputProps: { min: 100, max: 5000 } }}
                />
              </Box>
            )}
            {section.embedUrl && (
              <Alert severity="info" sx={{ mt: 1, fontSize: '0.75rem' }}>
                Preview: The embed will display at {section.embedHeight || 600}px height in the
                story.
              </Alert>
            )}
          </Box>

          <Box>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
            >
              <Box css={formLabelStyles(theme)} sx={{ mb: 0 }}>
                Buttons
              </Box>
              <Button size="small" startIcon={<Add />} onClick={handleAddButton}>
                Add Button
              </Button>
            </Box>
            {section.buttons && section.buttons.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {section.buttons.map((button, buttonIndex) => (
                  <Box
                    key={buttonIndex}
                    sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                      p: 1,
                      bgcolor: theme.palette.grey[50],
                      borderRadius: 1,
                    }}
                  >
                    <LinkIcon fontSize="small" color="action" />
                    <TextField
                      size="small"
                      placeholder="Button title"
                      value={button.title}
                      onChange={(e) => handleUpdateButton(buttonIndex, 'title', e.target.value)}
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      size="small"
                      placeholder="URL"
                      value={button.url}
                      onChange={(e) => handleUpdateButton(buttonIndex, 'url', e.target.value)}
                      sx={{ flex: 2 }}
                    />
                    <IconButton size="small" onClick={() => handleDeleteButton(buttonIndex)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.875rem',
                  fontStyle: 'italic',
                }}
              >
                No buttons added
              </Box>
            )}
          </Box>
        </Box>
      </AccordionDetails>
    </Accordion>
  )
}

export default SectionEditor
